/**
 * Profile Work Item API Service for Wadakaroo
 *
 * Covers all endpoints on:
 *   /api/profile-work-items          — ProfileWorkItem CRUD + activate/deactivate
 *   /api/profile-work-items/measurements — Measurement CRUD + activate/deactivate
 */

import axiosInstance from './axiosInstance';

const BASE = '/profile-work-items';
const MEAS = `${BASE}/measurements`;

// ============================================================
// Profile Work Items
// ============================================================

/**
 * GET /api/profile-work-items
 * @param {Object} params
 * @param {number}  params.page
 * @param {number}  params.size
 * @param {boolean} params.activeOnly
 */
export const getWorkItemsApi = async ({ page = 0, size = 10, activeOnly = false } = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        activeOnly: activeOnly.toString(),
    });
    const response = await axiosInstance.get(`${BASE}?${params}`);
    return response.data;
};

/**
 * GET /api/profile-work-items/{uuid}
 */
export const getWorkItemApi = async (uuid) => {
    const response = await axiosInstance.get(`${BASE}/${uuid}`);
    return response.data;
};

/**
 * POST /api/profile-work-items
 * @param {{ action, labourCost, estimatedHours, partUuids, measurementUuids }} data
 */
export const createWorkItemApi = async (data) => {
    const response = await axiosInstance.post(BASE, data);
    return response.data;
};

/**
 * PUT /api/profile-work-items/{uuid}
 * Replaces parts + measurements entirely.
 */
export const updateWorkItemApi = async (uuid, data) => {
    const response = await axiosInstance.put(`${BASE}/${uuid}`, data);
    return response.data;
};

/**
 * PATCH /api/profile-work-items/{uuid}/activate
 */
export const activateWorkItemApi = async (uuid) => {
    const response = await axiosInstance.patch(`${BASE}/${uuid}/activate`);
    return response.data;
};

/**
 * PATCH /api/profile-work-items/{uuid}/deactivate
 */
export const deactivateWorkItemApi = async (uuid) => {
    const response = await axiosInstance.patch(`${BASE}/${uuid}/deactivate`);
    return response.data;
};

/**
 * DELETE /api/profile-work-items/{uuid}
 */
export const deleteWorkItemApi = async (uuid) => {
    await axiosInstance.delete(`${BASE}/${uuid}`);
};

// ============================================================
// Measurements
// ============================================================

/**
 * GET /api/profile-work-items/measurements
 * Returns global + org-private measurements.
 */
export const getMeasurementsApi = async ({ page = 0, size = 100, activeOnly = false } = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        activeOnly: activeOnly.toString(),
    });
    const response = await axiosInstance.get(`${MEAS}?${params}`);
    return response.data;
};

/**
 * GET /api/profile-work-items/measurements/{uuid}
 */
export const getMeasurementApi = async (uuid) => {
    const response = await axiosInstance.get(`${MEAS}/${uuid}`);
    return response.data;
};

/**
 * POST /api/profile-work-items/measurements
 * @param {{ name, measurementType, config }} data
 */
export const createMeasurementApi = async (data) => {
    const response = await axiosInstance.post(MEAS, data);
    return response.data;
};

/**
 * PUT /api/profile-work-items/measurements/{uuid}
 * Only org-owned measurements.
 */
export const updateMeasurementApi = async (uuid, data) => {
    const response = await axiosInstance.put(`${MEAS}/${uuid}`, data);
    return response.data;
};

/**
 * PATCH /api/profile-work-items/measurements/{uuid}/activate
 */
export const activateMeasurementApi = async (uuid) => {
    const response = await axiosInstance.patch(`${MEAS}/${uuid}/activate`);
    return response.data;
};

/**
 * PATCH /api/profile-work-items/measurements/{uuid}/deactivate
 */
export const deactivateMeasurementApi = async (uuid) => {
    const response = await axiosInstance.patch(`${MEAS}/${uuid}/deactivate`);
    return response.data;
};

/**
 * DELETE /api/profile-work-items/measurements/{uuid}
 * Only org-owned measurements.
 */
export const deleteMeasurementApi = async (uuid) => {
    await axiosInstance.delete(`${MEAS}/${uuid}`);
};

// ============================================================
// Default export
// ============================================================

const profileWorkItemApi = {
    getWorkItemsApi,
    getWorkItemApi,
    createWorkItemApi,
    updateWorkItemApi,
    activateWorkItemApi,
    deactivateWorkItemApi,
    deleteWorkItemApi,
    getMeasurementsApi,
    getMeasurementApi,
    createMeasurementApi,
    updateMeasurementApi,
    activateMeasurementApi,
    deactivateMeasurementApi,
    deleteMeasurementApi,
};

export default profileWorkItemApi;